/**
 * Channel Settings view — Phase 2.3.j.4.
 *
 * Owns the Ticketing Configuration page's "Channel configurations"
 * section: per-channel cards (email, WhatsApp, PBX, INET-Go) with
 * config JSON / secrets editors, validate buttons, and per-channel
 * special UI (mailbox selector for email, PBX event picker, WhatsApp
 * CSAT inline section). The cluster also owns the shared
 * `channelSettingsState` that AI Prompts + Message Templates +
 * WhatsApp CSAT read from via getter helpers.
 *
 * Loaded as a module before admin.js. The IIFE registers
 * `window.adminViews.channelSettings`. The view-router calls `init()`
 * (which is `initChannelSettingsView` from the original code).
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml`, `openDialog`, `showFlash` (default already alias).
 *   - `hasRole` — for role-gated controls.
 *   - `formatDateTimeFull`, `formatDateTimeShort` — formatters.
 *   - `getChannelLabel` — uses `CHANNEL_LABEL_MAP` (still in admin.js).
 *   - `normalizePbxConfig`, `isValidTimeString` — shift management uses
 *     them too, so they stay.
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct
 * imports from src/lib. The constants formerly proxied through admin.js
 * (`CHANNEL_OPTIONS`, `DEFAULT_PBX_EVENTS`) are now imported directly
 * from `src/lib/channels.js`; `normalizePbxConfig` / `isValidTimeString`
 * come from `src/lib/pbx.js`.
 */

import { escapeHtml, formatDateTimeFull, formatDateTimeShort } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import { hasRole } from '../../src/lib/roles.js';
import { CHANNEL_OPTIONS, DEFAULT_PBX_EVENTS, getChannelLabel } from '../../src/lib/channels.js';
import { normalizePbxConfig, isValidTimeString } from '../../src/lib/pbx.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

  const adminAPI = api;
  const API = api.routes;
  const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // ---- Constants ------------------------------------------------------
  const NEW_MAILBOX_OPTION = '__new__';
  const PBX_EVENT_OPTIONS = [
    ['30012', '30012 – Call completed details'],
    ['30013', '30013 – Call ringing'],
    ['30015', '30015 – Call failure'],
    ['30016', '30016 – Incoming call request'],
    ['30017', '30017 – Agent answered'],
  ];

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let channelSettingsView, channelConfigsContainer, refreshConfigsBtn;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    channelSettingsView = document.getElementById('channelSettingsView');
    channelConfigsContainer = document.getElementById('channelConfigsContainer');
    refreshConfigsBtn = document.getElementById('refreshConfigsBtn');
  }

  // ---- State (owned by this module; shared by AI Prompts + Message
  // Templates + WhatsApp CSAT via `getState()`) ------------------------
  let channelSettingsInitialized = false;
  const channelSettingsState = {
    configs: [],
    templates: [],
    prompts: [],
    whatsappCsatPrompts: [],
    whatsappCsatDefaults: [],
    loadingConfigs: false,
    loadingTemplates: false,
    loadingPrompts: false,
    loadingWhatsAppCsat: false,
    whatsappCsatFetched: false,
    whatsappCsatLoadFailed: false,
    selectedMailboxes: {
      email: 'default',
    },
  };
  const channelStatusElements = new Map();
  const channelControlSync = new Map();

    function initChannelSettingsView() {
      if (!channelSettingsView) { console.warn('channel-settings: #channelSettingsView missing — view will not initialize.'); return; }
      if (!channelSettingsInitialized) {
        channelSettingsInitialized = true;
        if (channelConfigsContainer) {
          channelConfigsContainer.addEventListener('click', handleChannelConfigClick);
        }
        if (refreshConfigsBtn) {
          refreshConfigsBtn.addEventListener('click', () => {
            loadChannelConfigs().catch(() => {});
          });
        }
        // Templates listeners (refresh button, table click, create form,
        // filters, preview/test) and AI prompts listeners (container click,
        // refresh button, history list, history dialog close) are wired by
        // each module's `attachListeners`.
        if (adminViews.messageTemplates && adminViews.messageTemplates.attachListeners) {
          adminViews.messageTemplates.attachListeners();
        }
        if (adminViews.aiPrompts && adminViews.aiPrompts.attachListeners) {
          adminViews.aiPrompts.attachListeners();
        }
        // Region admin (form + list listeners + initial load) is owned by
        // `adminViews.tickets.regions` (Phase 2.1.a). mount() is idempotent.
        adminViews.tickets.regions.mount();
      }
      loadChannelConfigs().catch(() => {});
      if (adminViews.messageTemplates && adminViews.messageTemplates.loadMessageTemplates) {
        adminViews.messageTemplates.loadMessageTemplates().catch(() => {});
      }
      if (adminViews.aiPrompts && adminViews.aiPrompts.loadAiPrompts) {
        adminViews.aiPrompts.loadAiPrompts().catch(() => {});
      }
      // Even when the channel-settings inner block is skipped (e.g. on a
      // page that already has the section initialized), refresh region data.
      adminViews.tickets.regions.refresh().catch(() => {});
    }

    async function loadChannelConfigs() {
      if (!channelConfigsContainer) { console.warn('channel-settings: #channelConfigsContainer missing — configs will not load.'); return []; }
      channelSettingsState.loadingConfigs = true;
      renderChannelConfigs();
      try {
        const endpoint = `${API.adminChannelConfigs}?include_secrets=true`;
        const response = await adminAPI.fetch(endpoint, { method: 'GET' });
        const items = Array.isArray(response?.items) ? response.items : (Array.isArray(response) ? response : []);
        channelSettingsState.configs = items;
        const emailConfigs = items.filter((item) => item && item.channel === 'email');
        const availableKeys = emailConfigs.map((item) => (item && item.mailbox_key) || 'default');
        const currentKey = channelSettingsState.selectedMailboxes.email;
        if (availableKeys.length === 0) {
          channelSettingsState.selectedMailboxes.email = NEW_MAILBOX_OPTION;
        } else if (!availableKeys.includes(currentKey)) {
          channelSettingsState.selectedMailboxes.email = availableKeys.includes('default') ? 'default' : availableKeys[0];
        }
      } catch (err) {
        showFlash(err.message || 'Failed to load channel configurations', 'error');
        channelSettingsState.configs = [];
      } finally {
        channelSettingsState.loadingConfigs = false;
        renderChannelConfigs();
      }
      return channelSettingsState.configs;
    }

    // ===== WhatsApp CSAT prompts (Phase 2.3.j.3) =====
    // cloneWhatsAppPrompt, getWhatsAppCsatPrompts, renderWhatsAppCsatPrompts,
    // loadWhatsAppCsatPrompts, reloadWhatsAppCsatPrompts,
    // addWhatsAppCsatPrompt, removeWhatsAppCsatPrompt,
    // resetWhatsAppCsatPromptsToDefaults, saveWhatsAppCsatPrompts moved
    // into `frontend/admin/js/views/whatsapp-csat.js`. Channel-settings
    // call them via `adminViews.whatsappCsat.{render,load}`.

    function buildChannelConfigMeta(existing) {
    const mailbox = existing && existing.mailbox_key ? `Mailbox: ${existing.mailbox_key} · ` : '';
    const updatedLabel = existing && (existing.updated_at || existing.created_at)
        ? formatDateTimeFull(existing.updated_at || existing.created_at)
        : '—';
      const validatedLabel = existing && existing.last_validated_at ? formatDateTimeFull(existing.last_validated_at) : 'Never';
      const validatedBy = existing && existing.validated_by_user_id != null ? `User #${existing.validated_by_user_id}` : '—';
      const secretsStatus = existing && existing.has_secrets ? 'Secrets stored' : 'No secrets saved';
    return `${mailbox}Last updated: ${updatedLabel} · Last validated: ${validatedLabel} (by ${validatedBy}) · Secrets: ${secretsStatus}`;
  }

    function buildSecretsHint(hasSecretsSaved) {
      if (hasSecretsSaved) {
        return 'Secrets loaded. Update values as needed, or clear the field to remove stored credentials.';
      }
      return 'No secrets saved yet. Provide credentials in JSON format.';
    }

    function setChannelStatus(channel, message, tone = 'info') {
      const el = channelStatusElements.get(channel);
      if (!el) return;
      const hasMessage = Boolean(message);
      el.textContent = hasMessage ? message : '';
      el.className = `config-status ${tone}`;
      el.hidden = !hasMessage;
    }

    function upsertChannelConfigState(updated) {
      if (!updated || !updated.channel) return;
      const configs = Array.isArray(channelSettingsState.configs) ? [...channelSettingsState.configs] : [];
      const targetMailbox = (updated.mailbox_key || 'default');
      const idx = configs.findIndex((item) => (
        item
        && item.channel === updated.channel
        && ((item.mailbox_key || 'default') === targetMailbox)
      ));
      const normalized = Object.assign({}, configs[idx] || {}, updated);
      if (idx >= 0) {
        configs[idx] = normalized;
      } else {
        configs.push(normalized);
      }
      channelSettingsState.configs = configs;
    }

    function updateChannelConfigCard(updated) {
      if (!channelConfigsContainer || !updated || !updated.channel) return;
      const details = channelConfigsContainer.querySelector(`details[data-channel="${updated.channel}"]`);
      if (!details) return;
      const configTextarea = details.querySelector(`textarea[data-config-input="${updated.channel}"]`);
      const configForDisplay = updated.channel === 'pbx'
        ? normalizePbxConfig(updated.config || {})
        : (updated.config || {});
      if (configTextarea) {
        const newValue = JSON.stringify(configForDisplay || {}, null, 2);
        if (configTextarea.value !== newValue) {
          configTextarea.value = newValue;
        }
      }
      const meta = details.querySelector(`[data-config-meta="${updated.channel}"]`);
      if (meta) {
        meta.textContent = buildChannelConfigMeta(updated);
      }
      const hint = details.querySelector(`[data-secrets-hint="${updated.channel}"]`);
      if (hint) {
        hint.textContent = buildSecretsHint(Boolean(updated.has_secrets));
      }
      const secretsTextarea = details.querySelector(`textarea[data-secrets-input="${updated.channel}"]`);
      if (secretsTextarea) {
        if (updated && updated.secrets) {
          try {
            secretsTextarea.value = JSON.stringify(updated.secrets, null, 2);
          } catch (err) {
            secretsTextarea.value = '';
          }
        } else {
          secretsTextarea.value = '';
        }
      }
      if (updated.channel === 'pbx') {
        const syncFn = channelControlSync.get('pbx');
        if (typeof syncFn === 'function') {
          try {
            syncFn(configForDisplay);
          } catch (err) {
            console.warn('Failed to sync PBX controls', err);
          }
        }
        if (updated.last_validated_at) {
          setChannelStatus('pbx', `PBX token validated ${formatDateTimeFull(updated.last_validated_at)}`, 'success');
        } else {
          setChannelStatus('pbx', 'PBX token has not been validated.', 'warning');
        }
      }
    }

    function renderChannelConfigs() {
      if (!channelConfigsContainer) return;
      channelConfigsContainer.innerHTML = '';
      channelStatusElements.clear();
      channelControlSync.clear();
      if (channelSettingsState.loadingConfigs) {
        channelConfigsContainer.innerHTML = '<div style="color:var(--sub);">Loading configurations…</div>';
        return;
      }
      if (!CHANNEL_OPTIONS.length) {
        channelConfigsContainer.innerHTML = '<div style="color:var(--sub);">No channels configured.</div>';
        return;
      }
      const configs = Array.isArray(channelSettingsState.configs) ? channelSettingsState.configs : [];
      CHANNEL_OPTIONS.forEach((option) => {
        const configsForChannel = configs.filter((item) => item && item.channel === option.key);
        let selectedMailboxKey = option.key === 'email'
          ? channelSettingsState.selectedMailboxes.email
          : null;
        if (option.key === 'email') {
          const availableKeys = configsForChannel.map((item) => (item && item.mailbox_key) || 'default');
          if (!availableKeys.length && selectedMailboxKey !== NEW_MAILBOX_OPTION) {
            selectedMailboxKey = NEW_MAILBOX_OPTION;
            channelSettingsState.selectedMailboxes.email = NEW_MAILBOX_OPTION;
          } else if (availableKeys.length && selectedMailboxKey !== NEW_MAILBOX_OPTION && !availableKeys.includes(selectedMailboxKey)) {
            selectedMailboxKey = availableKeys.includes('default') ? 'default' : availableKeys[0];
            channelSettingsState.selectedMailboxes.email = selectedMailboxKey;
          }
        }
        let existing = option.key === 'email'
          ? (selectedMailboxKey && selectedMailboxKey !== NEW_MAILBOX_OPTION
            ? (configsForChannel.find((item) => ((item && item.mailbox_key) || 'default') === selectedMailboxKey) || null)
            : null)
          : (configsForChannel[0] || null);
        const details = document.createElement('details');
        details.dataset.channel = option.key;
        details.open = true;
        details.style.border = '1px solid rgba(148, 163, 184, 0.3)';
        details.style.borderRadius = '12px';
        details.style.padding = '12px';
        details.style.background = 'rgba(15, 23, 42, 0.55)';
        details.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.35)';

        const summary = document.createElement('summary');
        summary.textContent = `${option.label} settings`;
        summary.style.fontWeight = '600';
        details.appendChild(summary);

        if (option.description) {
          const desc = document.createElement('p');
          desc.textContent = option.description;
          desc.style.margin = '8px 0';
          desc.style.color = 'var(--sub)';
          desc.style.fontSize = '12px';
          details.appendChild(desc);
        }

        const meta = document.createElement('p');
        meta.style.margin = '4px 0 12px';
        meta.style.color = 'var(--sub)';
        meta.style.fontSize = '12px';
        meta.dataset.configMeta = option.key;
        meta.textContent = buildChannelConfigMeta(existing);
        if (option.key === 'email' && channelSettingsState.selectedMailboxes.email === NEW_MAILBOX_OPTION) {
          meta.textContent = 'Mailbox: (new mailbox) · No settings saved yet.';
        }
        details.appendChild(meta);

        const status = document.createElement('div');
        status.className = 'config-status';
        status.dataset.statusChannel = option.key;
        status.style.margin = '0 0 12px';
        status.style.fontSize = '12px';
        status.style.fontWeight = '600';
        status.setAttribute('role', 'status');
        details.appendChild(status);
        channelStatusElements.set(option.key, status);
        if (option.key === 'pbx') {
          if (existing && existing.last_validated_at) {
            setChannelStatus(option.key, `PBX token validated ${formatDateTimeFull(existing.last_validated_at)}`, 'success');
          } else {
            setChannelStatus(option.key, 'PBX token has not been validated.', 'warning');
          }
        } else {
          status.style.display = 'none';
          status.hidden = true;
        }

        if (option.key === 'email') {
          const mailboxWrap = document.createElement('div');
          mailboxWrap.style.display = 'grid';
          mailboxWrap.style.gap = '8px';
          mailboxWrap.style.marginBottom = '12px';

          const mailboxLabel = document.createElement('label');
          mailboxLabel.className = 'field';
          mailboxLabel.style.margin = '0';
          const mailboxTitle = document.createElement('span');
          mailboxTitle.textContent = 'Mailbox';
          mailboxLabel.appendChild(mailboxTitle);
          const mailboxSelect = document.createElement('select');
          mailboxSelect.dataset.mailboxSelector = option.key;
          const mailboxOptions = configsForChannel.map((item) => {
            const key = (item && item.mailbox_key) || 'default';
            const sender = item && item.config && item.config.sender ? String(item.config.sender) : '';
            const label = sender ? `${key} – ${sender}` : key;
            return { key, label };
          }).sort((a, b) => {
            if (a.key === 'default') return -1;
            if (b.key === 'default') return 1;
            return a.key.localeCompare(b.key);
          });
          mailboxOptions.forEach((mailbox) => {
            const opt = document.createElement('option');
            opt.value = mailbox.key;
            opt.textContent = mailbox.label;
            mailboxSelect.appendChild(opt);
          });
          const newOption = document.createElement('option');
          newOption.value = NEW_MAILBOX_OPTION;
          newOption.textContent = '➕ New mailbox…';
          mailboxSelect.appendChild(newOption);
          const effectiveSelection = selectedMailboxKey || (mailboxOptions[0] && mailboxOptions[0].key) || NEW_MAILBOX_OPTION;
          mailboxSelect.value = effectiveSelection;
          if (mailboxSelect.value !== effectiveSelection) {
            mailboxSelect.value = NEW_MAILBOX_OPTION;
            channelSettingsState.selectedMailboxes.email = NEW_MAILBOX_OPTION;
            existing = null;
          } else {
            channelSettingsState.selectedMailboxes.email = mailboxSelect.value;
            if (mailboxSelect.value === NEW_MAILBOX_OPTION) {
              existing = null;
            } else {
              existing = configsForChannel.find((item) => ((item && item.mailbox_key) || 'default') === mailboxSelect.value) || null;
            }
          }
          mailboxSelect.addEventListener('change', (evt) => {
            channelSettingsState.selectedMailboxes.email = evt.target.value;
            renderChannelConfigs();
          });
          mailboxLabel.appendChild(mailboxSelect);
          mailboxWrap.appendChild(mailboxLabel);

          const keyLabel = document.createElement('label');
          keyLabel.className = 'field';
          keyLabel.style.margin = '0';
          const keyTitle = document.createElement('span');
          keyTitle.textContent = 'Mailbox key';
          keyLabel.appendChild(keyTitle);
          const keyInput = document.createElement('input');
          keyInput.type = 'text';
          keyInput.dataset.mailboxKeyInput = option.key;
          keyInput.placeholder = 'e.g. billing';
          if (channelSettingsState.selectedMailboxes.email === NEW_MAILBOX_OPTION) {
            keyInput.value = '';
            keyInput.readOnly = false;
            keyInput.required = true;
          } else {
            keyInput.value = channelSettingsState.selectedMailboxes.email || 'default';
            keyInput.readOnly = true;
            keyInput.required = true;
            keyInput.title = 'Mailbox keys cannot be changed. Select "New mailbox…" to add another inbox.';
          }
          keyLabel.appendChild(keyInput);
          const keyHint = document.createElement('p');
          keyHint.style.fontSize = '11px';
          keyHint.style.color = 'var(--sub)';
          keyHint.style.margin = '4px 0 0';
          keyHint.textContent = 'Choose a short identifier (letters, numbers, dashes, underscores). Example: billing, sales, noc.';
          keyLabel.appendChild(keyHint);
          mailboxWrap.appendChild(keyLabel);
          details.appendChild(mailboxWrap);
        }

        const configLabel = document.createElement('label');
        configLabel.style.display = 'grid';
        configLabel.style.gap = '4px';
        configLabel.style.fontSize = '12px';
        configLabel.textContent = 'Config JSON';
        const configTextarea = document.createElement('textarea');
        configTextarea.rows = 8;
        configTextarea.dataset.configInput = option.key;
        if (option.key === 'email') {
          configTextarea.dataset.mailboxKey = channelSettingsState.selectedMailboxes.email || 'default';
        }
        let configObject = (existing && existing.config && typeof existing.config === 'object' && !Array.isArray(existing.config))
          ? { ...existing.config }
          : {};
        if (option.key === 'pbx') {
          configObject = normalizePbxConfig(configObject);
        }
        try {
          configTextarea.value = JSON.stringify(configObject, null, 2);
        } catch (err) {
          const fallback = option.key === 'pbx' ? normalizePbxConfig({}) : {};
          configTextarea.value = JSON.stringify(fallback, null, 2);
          configObject = fallback;
        }
        configTextarea.style.width = '100%';
        configTextarea.style.minHeight = '160px';
        configTextarea.style.fontFamily = 'monospace';
        configTextarea.spellcheck = false;
        configLabel.appendChild(configTextarea);
        details.appendChild(configLabel);

        const parseConfigTextarea = () => {
          try {
            return JSON.parse(configTextarea.value || '{}');
          } catch (err) {
            return null;
          }
        };

        if (option.key === 'app') {
          const gpsLabel = document.createElement('label');
          gpsLabel.className = 'field';
          gpsLabel.style.marginTop = '12px';
          const gpsTitle = document.createElement('span');
          gpsTitle.textContent = 'GPS accuracy threshold (meters)';
          gpsLabel.appendChild(gpsTitle);
          const gpsInput = document.createElement('input');
          gpsInput.type = 'number';
          gpsInput.min = '1';
          gpsInput.step = '0.1';
          gpsInput.placeholder = 'e.g. 10';
          gpsInput.dataset.gpsThresholdInput = option.key;
          const initialThreshold = Number(configObject.gps_accuracy_threshold_meters);
          const defaultThreshold = Number.isFinite(initialThreshold) && initialThreshold > 0 ? initialThreshold : 10;
          gpsInput.value = String(defaultThreshold);
          gpsLabel.appendChild(gpsInput);
          const gpsHint = document.createElement('p');
          gpsHint.style.margin = '4px 0 0';
          gpsHint.style.fontSize = '11px';
          gpsHint.style.color = 'var(--sub)';
          gpsHint.textContent = 'Used to validate technician resolve submissions. Default is 10 meters.';
          gpsLabel.appendChild(gpsHint);
          details.appendChild(gpsLabel);

          gpsInput.addEventListener('change', () => {
            const parsed = parseConfigTextarea();
            if (!parsed) return;
            const valueRaw = parseFloat(gpsInput.value);
            if (!Number.isFinite(valueRaw) || valueRaw <= 0) {
              gpsInput.value = String(defaultThreshold);
              showFlash('GPS accuracy must be greater than zero.', 'error');
              return;
            }
            parsed.gps_accuracy_threshold_meters = Number(valueRaw.toFixed(1));
            configTextarea.value = JSON.stringify(parsed, null, 2);
          });

          configTextarea.addEventListener('input', () => {
            const parsed = parseConfigTextarea();
            if (!parsed) return;
            const nextValue = Number(parsed.gps_accuracy_threshold_meters);
            if (Number.isFinite(nextValue) && nextValue > 0) {
              gpsInput.value = String(nextValue);
            }
          });
        }

        if (option.key === 'pbx') {
          const updateConfig = (mutator) => {
            const parsed = parseConfigTextarea();
            if (!parsed) return;
            const next = mutator({ ...parsed });
            try {
              configTextarea.value = JSON.stringify(next, null, 2);
            } catch (err) {
              console.warn('Failed to serialise PBX config', err);
            }
          };

          const pbxSection = document.createElement('div');
          pbxSection.style.display = 'grid';
          pbxSection.style.gap = '12px';
          pbxSection.style.marginTop = '12px';

          // ---- Connection (scheme / TLS) ----
          const connTitle = document.createElement('span');
          connTitle.textContent = 'Connection';
          connTitle.style.fontWeight = '600';
          pbxSection.appendChild(connTitle);

          const baseUrlLabel = document.createElement('label');
          baseUrlLabel.className = 'field';
          const baseUrlTitle = document.createElement('span');
          baseUrlTitle.textContent = 'Base URL (optional)';
          baseUrlLabel.appendChild(baseUrlTitle);
          const baseUrlInput = document.createElement('input');
          baseUrlInput.type = 'text';
          baseUrlInput.dataset.pbxBaseUrl = 'true';
          baseUrlInput.placeholder = 'https://192.168.120.11:8088';
          baseUrlLabel.appendChild(baseUrlInput);
          const baseUrlHint = document.createElement('p');
          baseUrlHint.style.fontSize = '11px';
          baseUrlHint.style.color = 'var(--sub)';
          baseUrlHint.textContent = 'Full PBX OpenAPI URL incl. scheme (e.g. https://host:8088). If set, it overrides "Use HTTPS" and host/port; the WebSocket scheme (ws/wss) follows it.';
          baseUrlLabel.appendChild(baseUrlHint);
          pbxSection.appendChild(baseUrlLabel);

          const connToggleRow = document.createElement('div');
          connToggleRow.style.display = 'grid';
          connToggleRow.style.gridTemplateColumns = 'repeat(auto-fit, minmax(220px, 1fr))';
          connToggleRow.style.gap = '12px';

          const httpsLabel = document.createElement('label');
          httpsLabel.style.display = 'flex';
          httpsLabel.style.alignItems = 'center';
          httpsLabel.style.gap = '8px';
          const httpsCheckbox = document.createElement('input');
          httpsCheckbox.type = 'checkbox';
          httpsCheckbox.dataset.pbxUseHttps = 'true';
          const httpsText = document.createElement('span');
          httpsText.textContent = 'Use HTTPS (when no Base URL)';
          httpsLabel.appendChild(httpsCheckbox);
          httpsLabel.appendChild(httpsText);
          connToggleRow.appendChild(httpsLabel);

          const verifyLabel = document.createElement('label');
          verifyLabel.style.display = 'flex';
          verifyLabel.style.alignItems = 'center';
          verifyLabel.style.gap = '8px';
          const verifyCheckbox = document.createElement('input');
          verifyCheckbox.type = 'checkbox';
          verifyCheckbox.dataset.pbxVerifySsl = 'true';
          const verifyText = document.createElement('span');
          verifyText.textContent = 'Verify TLS certificate';
          verifyLabel.appendChild(verifyCheckbox);
          verifyLabel.appendChild(verifyText);
          connToggleRow.appendChild(verifyLabel);

          const connHint = document.createElement('p');
          connHint.style.gridColumn = '1 / -1';
          connHint.style.fontSize = '11px';
          connHint.style.color = 'var(--sub)';
          connHint.textContent = 'Turn off certificate verification for self-signed / internal PBX certs (otherwise TLS calls fail with "certificate verify failed").';
          connToggleRow.appendChild(connHint);

          pbxSection.appendChild(connToggleRow);

          const eventLabel = document.createElement('label');
          eventLabel.className = 'field';
          const eventTitle = document.createElement('span');
          eventTitle.textContent = 'Subscribed PBX events';
          eventLabel.appendChild(eventTitle);
          const eventSelect = document.createElement('select');
          eventSelect.multiple = true;
          eventSelect.dataset.pbxEvents = 'true';
          eventSelect.size = Math.min(PBX_EVENT_OPTIONS.length, 6);
          PBX_EVENT_OPTIONS.forEach(([value, label]) => {
            const opt = document.createElement('option');
            opt.value = value;
            opt.textContent = label;
            eventSelect.appendChild(opt);
          });
          const eventHint = document.createElement('p');
          eventHint.style.fontSize = '11px';
          eventHint.style.color = 'var(--sub)';
          eventHint.textContent = 'Defaults are 30015, 30016, 30012. Select additional events to subscribe to.';
          eventLabel.appendChild(eventSelect);
          eventLabel.appendChild(eventHint);
          pbxSection.appendChild(eventLabel);

          const trunksCard = document.createElement('div');
          trunksCard.style.display = 'grid';
          trunksCard.style.gap = '6px';
          const trunksHeader = document.createElement('div');
          trunksHeader.style.display = 'flex';
          trunksHeader.style.alignItems = 'center';
          trunksHeader.style.justifyContent = 'space-between';
          const trunksTitle = document.createElement('span');
          trunksTitle.textContent = 'Staffing trunks';
          trunksTitle.style.fontWeight = '600';
          trunksHeader.appendChild(trunksTitle);
          const trunksSyncBtn = document.createElement('button');
          trunksSyncBtn.type = 'button';
          trunksSyncBtn.className = 'btn';
          trunksSyncBtn.textContent = 'Get trunks';
          trunksHeader.appendChild(trunksSyncBtn);
          trunksCard.appendChild(trunksHeader);

          const trunksStatus = document.createElement('p');
          trunksStatus.style.margin = '0';
          trunksStatus.style.fontSize = '11px';
          trunksStatus.style.color = 'var(--sub)';
          trunksCard.appendChild(trunksStatus);

          const trunksList = document.createElement('ul');
          trunksList.style.listStyle = 'none';
          trunksList.style.margin = '0';
          trunksList.style.padding = '0';
          trunksList.style.display = 'none';
          trunksList.style.gap = '4px';
          trunksCard.appendChild(trunksList);

          const trunksEmpty = document.createElement('p');
          trunksEmpty.style.margin = '0';
          trunksEmpty.style.fontSize = '12px';
          trunksEmpty.style.color = 'var(--sub)';
          trunksEmpty.textContent = 'No trunks synced. Click “Get trunks” to load from the PBX.';
          trunksCard.appendChild(trunksEmpty);

          const trunksMeta = document.createElement('p');
          trunksMeta.style.margin = '0';
          trunksMeta.style.fontSize = '11px';
          trunksMeta.style.color = 'var(--sub)';
          trunksMeta.style.display = 'none';
          trunksCard.appendChild(trunksMeta);

          const trunksHint = document.createElement('p');
          trunksHint.style.margin = '0';
          trunksHint.style.fontSize = '11px';
          trunksHint.style.color = 'var(--sub)';
          trunksHint.textContent = 'Trunks are fetched directly from the PBX and stored with their outbound caller ID.';
          trunksCard.appendChild(trunksHint);

          const setTrunksStatus = (message, tone = 'info') => {
            if (!message) {
              trunksStatus.textContent = '';
              return;
            }
            trunksStatus.textContent = message;
            if (tone === 'error') {
              trunksStatus.style.color = 'var(--danger, #ef4444)';
            } else if (tone === 'success') {
              trunksStatus.style.color = 'var(--success, #22c55e)';
            } else {
              trunksStatus.style.color = 'var(--sub)';
            }
          };

          const renderTrunks = (details, names, syncedAt) => {
            const baseList = Array.isArray(details) && details.length
              ? details
              : Array.isArray(names)
                ? names.map((name) => ({ name }))
                : [];
            trunksList.innerHTML = '';
            if (!baseList.length) {
              trunksList.style.display = 'none';
              trunksEmpty.style.display = '';
            } else {
              trunksList.style.display = 'grid';
              trunksEmpty.style.display = 'none';
              baseList.forEach((entry) => {
                if (!entry || !entry.name) return;
                const item = document.createElement('li');
                item.textContent = entry.def_outbound_cid
                  ? `${entry.name} (${entry.def_outbound_cid})`
                  : entry.name;
                item.style.fontSize = '13px';
                item.style.padding = '2px 0';
                trunksList.appendChild(item);
              });
            }
            if (syncedAt) {
              trunksMeta.textContent = `Last synced ${formatDateTimeShort(syncedAt)}`;
              trunksMeta.style.display = '';
            } else {
              trunksMeta.textContent = '';
              trunksMeta.style.display = 'none';
            }
          };

          trunksSyncBtn.addEventListener('click', async () => {
            const originalText = trunksSyncBtn.textContent;
            trunksSyncBtn.disabled = true;
            trunksSyncBtn.textContent = 'Fetching…';
            setTrunksStatus('Fetching trunks from PBX…', 'info');
            try {
              const response = await adminAPI.fetch(API.adminPbxSyncTrunks, { method: 'POST' });
              const items = Array.isArray(response?.items) ? response.items.filter((item) => item && item.name) : [];
              const normalizedItems = items.map((item) => ({
                name: item.name,
                def_outbound_cid: item.def_outbound_cid || null,
              }));
              const syncedAt = new Date().toISOString();
              updateConfig((current) => normalizePbxConfig({
                ...current,
                staffing_trunks: normalizedItems.map((item) => item.name),
                staffing_trunk_details: normalizedItems,
                staffing_trunks_synced_at: syncedAt,
              }));
              renderTrunks(normalizedItems, null, syncedAt);
              setTrunksStatus(
                normalizedItems.length
                  ? `Loaded ${normalizedItems.length} trunk${normalizedItems.length === 1 ? '' : 's'} from PBX.`
                  : 'PBX did not return any trunks.',
                normalizedItems.length ? 'success' : 'info',
              );
            } catch (err) {
              setTrunksStatus(err && err.message ? err.message : 'Failed to fetch PBX trunks.', 'error');
              showFlash(err && err.message ? err.message : 'Failed to fetch PBX trunks.', 'error');
            } finally {
              trunksSyncBtn.disabled = false;
              trunksSyncBtn.textContent = originalText;
            }
          });

          pbxSection.appendChild(trunksCard);

          const retentionLabel = document.createElement('label');
          retentionLabel.className = 'field';
          const retentionTitle = document.createElement('span');
          retentionTitle.textContent = 'Recording retention (days)';
          retentionLabel.appendChild(retentionTitle);
          const retentionInput = document.createElement('input');
          retentionInput.type = 'number';
          retentionInput.dataset.pbxRetention = 'true';
          retentionInput.min = '1';
          retentionInput.step = '1';
          const retentionHint = document.createElement('p');
          retentionHint.style.fontSize = '11px';
          retentionHint.style.color = 'var(--sub)';
          retentionHint.textContent = 'Default retention is 365 days. Adjust to match PBX policy.';
          retentionLabel.appendChild(retentionInput);
          retentionLabel.appendChild(retentionHint);
          pbxSection.appendChild(retentionLabel);

          const staffingRow = document.createElement('div');
          staffingRow.style.display = 'grid';
          staffingRow.style.gridTemplateColumns = 'repeat(auto-fit, minmax(140px, 1fr))';
          staffingRow.style.gap = '12px';

          const startLabel = document.createElement('label');
          startLabel.className = 'field';
          const startTitle = document.createElement('span');
          startTitle.textContent = 'Staffing start (HH:MM)';
          startLabel.appendChild(startTitle);
          const startInput = document.createElement('input');
          startInput.type = 'time';
          startInput.dataset.pbxStaffing = 'start';
          startLabel.appendChild(startInput);
          staffingRow.appendChild(startLabel);

          const endLabel = document.createElement('label');
          endLabel.className = 'field';
          const endTitle = document.createElement('span');
          endTitle.textContent = 'Staffing end (HH:MM)';
          endLabel.appendChild(endTitle);
          const endInput = document.createElement('input');
          endInput.type = 'time';
          endInput.dataset.pbxStaffing = 'end';
          endLabel.appendChild(endInput);
          staffingRow.appendChild(endLabel);

          const staffingHint = document.createElement('p');
          staffingHint.style.gridColumn = '1 / -1';
          staffingHint.style.fontSize = '11px';
          staffingHint.style.color = 'var(--sub)';
          staffingHint.textContent = 'Defines expected staffed hours to surface compliance warnings (default 09:00–23:00).';
          staffingRow.appendChild(staffingHint);

          pbxSection.appendChild(staffingRow);

          const syncPbxControls = (rawConfig) => {
            const normalized = normalizePbxConfig(rawConfig);
            baseUrlInput.value = typeof normalized.base_url === 'string' ? normalized.base_url : '';
            const httpsImplied = Boolean(normalized.use_https) || /^https:/i.test(String(normalized.base_url || ''));
            httpsCheckbox.checked = Boolean(normalized.use_https);
            // verify_ssl mirrors the backend default (derived from scheme) until set explicitly.
            verifyCheckbox.checked = ('verify_ssl' in normalized) ? Boolean(normalized.verify_ssl) : httpsImplied;
            const selectedCodes = Array.isArray(normalized.event_codes)
              ? normalized.event_codes.map((code) => String(code))
              : [...DEFAULT_PBX_EVENTS];
            Array.from(eventSelect.options).forEach((opt) => {
              opt.selected = selectedCodes.includes(opt.value);
            });
            const days = Number(normalized.recording_retention_days);
            retentionInput.value = String(Math.max(1, Number.isFinite(days) ? Math.round(days) : 365));
            startInput.value = normalized.staffing_start;
            endInput.value = normalized.staffing_end;
            renderTrunks(
              normalized.staffing_trunk_details || [],
              normalized.staffing_trunks || [],
              normalized.staffing_trunks_synced_at || null,
            );
            const trunkCount = Array.isArray(normalized.staffing_trunks) ? normalized.staffing_trunks.length : 0;
            if (trunkCount) {
              setTrunksStatus(`Configured ${trunkCount} trunk${trunkCount === 1 ? '' : 's'}.`, 'success');
            } else {
              setTrunksStatus('No trunks configured. Use “Get trunks” to load from PBX.', 'info');
            }
          };

          const updatePbxConfig = (mutator) => {
            updateConfig((current) => normalizePbxConfig(mutator(current)));
            const parsed = parseConfigTextarea();
            if (parsed) {
              syncPbxControls(parsed);
            }
          };

          eventSelect.addEventListener('change', () => {
            const selected = Array.from(eventSelect.selectedOptions).map((opt) => opt.value);
            updatePbxConfig((next) => {
              next.event_codes = selected.length ? selected : [...DEFAULT_PBX_EVENTS];
              return next;
            });
          });

          retentionInput.addEventListener('change', () => {
            let parsedValue = parseInt(retentionInput.value, 10);
            if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
              parsedValue = 365;
              retentionInput.value = '365';
              showFlash('Recording retention must be a positive number of days.', 'error');
            }
            const valueToSet = parsedValue;
            updatePbxConfig((next) => {
              next.recording_retention_days = valueToSet;
              return next;
            });
          });

          const handleStaffingChange = (inputEl, key, fallback) => {
            inputEl.addEventListener('change', () => {
              let value = inputEl.value || '';
              if (!isValidTimeString(value)) {
                value = fallback;
                inputEl.value = value;
                showFlash('Staffing time must be in HH:MM format.', 'error');
              }
              const timeValue = value;
              updatePbxConfig((next) => {
                next[key] = timeValue;
                return next;
              });
            });
          };

          handleStaffingChange(startInput, 'staffing_start', '09:00');
          handleStaffingChange(endInput, 'staffing_end', '23:00');

          baseUrlInput.addEventListener('change', () => {
            const value = baseUrlInput.value.trim();
            updatePbxConfig((next) => {
              if (value) next.base_url = value; else delete next.base_url;
              return next;
            });
          });
          httpsCheckbox.addEventListener('change', () => {
            const checked = httpsCheckbox.checked;
            updatePbxConfig((next) => { next.use_https = checked; return next; });
          });
          verifyCheckbox.addEventListener('change', () => {
            const checked = verifyCheckbox.checked;
            updatePbxConfig((next) => { next.verify_ssl = checked; return next; });
          });

          configTextarea.addEventListener('input', () => {
            const parsed = parseConfigTextarea();
            if (!parsed) return;
            syncPbxControls(parsed);
          });

          syncPbxControls(configObject);
          channelControlSync.set(option.key, syncPbxControls);
          details.appendChild(pbxSection);
        }

        if (option.key === 'whatsapp') {
          const csat = window.adminViews && window.adminViews.whatsappCsat;
          if (csat) {
            if (
              !channelSettingsState.whatsappCsatFetched
              && !channelSettingsState.loadingWhatsAppCsat
              && !channelSettingsState.whatsappCsatLoadFailed
            ) {
              csat.load();
            }
            csat.render(details);
          }
        }

        const secretsLabel = document.createElement('label');
        secretsLabel.style.display = 'grid';
        secretsLabel.style.gap = '4px';
        secretsLabel.style.fontSize = '12px';
        secretsLabel.style.marginTop = '12px';
        secretsLabel.textContent = 'Secrets JSON';
        const secretsTextarea = document.createElement('textarea');
        secretsTextarea.rows = 6;
        secretsTextarea.dataset.secretsInput = option.key;
        secretsTextarea.placeholder = '{ "api_key": "..." }';
        secretsTextarea.style.width = '100%';
        secretsTextarea.style.minHeight = '120px';
        secretsTextarea.style.fontFamily = 'monospace';
        secretsTextarea.spellcheck = false;
        if (option.key === 'email') {
          secretsTextarea.dataset.mailboxKey = channelSettingsState.selectedMailboxes.email || 'default';
        }
        if (existing && existing.secrets) {
          try {
            secretsTextarea.value = JSON.stringify(existing.secrets, null, 2);
          } catch (err) {
            secretsTextarea.value = '';
          }
        }
        secretsLabel.appendChild(secretsTextarea);
        details.appendChild(secretsLabel);

        const hint = document.createElement('p');
        hint.dataset.secretsHint = option.key;
        hint.textContent = buildSecretsHint(Boolean(existing && existing.has_secrets));
        hint.style.color = 'var(--sub)';
        hint.style.fontSize = '11px';
        hint.style.margin = '6px 0 12px';
        details.appendChild(hint);

        const actions = document.createElement('div');
        actions.className = 'row';
        actions.style.gap = '8px';
        const saveBtn = document.createElement('button');
        saveBtn.className = 'btn';
        saveBtn.type = 'button';
        saveBtn.dataset.configSave = option.key;
        saveBtn.textContent = 'Save';
        const testBtn = document.createElement('button');
        testBtn.className = 'btn primary';
        testBtn.type = 'button';
        testBtn.dataset.configValidate = option.key;
        testBtn.textContent = 'Save & Test';
        actions.appendChild(saveBtn);
        actions.appendChild(testBtn);
        details.appendChild(actions);

        channelConfigsContainer.appendChild(details);
      });
    }


    function handleChannelConfigClick(event) {
      const btn = event.target.closest('button[data-config-save], button[data-config-validate]');
      if (!btn) return;
      const channel = btn.dataset.configSave || btn.dataset.configValidate;
      if (!channel) return;
      const validate = Boolean(btn.dataset.configValidate);
      handleChannelConfigSave(channel, validate);
    }

    async function handleChannelConfigSave(channel, validate) {
      if (!channelConfigsContainer) {
        // eslint-disable-next-line no-console
        console.warn('handleChannelConfigSave: #channelConfigsContainer missing — Save/Test click had no effect.');
        return;
      }
      const configInput = channelConfigsContainer.querySelector(`textarea[data-config-input="${channel}"]`);
      const secretsInput = channelConfigsContainer.querySelector(`textarea[data-secrets-input="${channel}"]`);
      const mailboxKeyInput = channel === 'email'
        ? channelConfigsContainer.querySelector('[data-mailbox-key-input="email"]')
        : null;
      const mailboxSelector = channel === 'email'
        ? channelConfigsContainer.querySelector('[data-mailbox-selector="email"]')
        : null;
      if (!configInput) return;
      const configText = (configInput.value || '').trim();
      let config;
      try {
        config = configText ? JSON.parse(configText) : {};
      } catch (err) {
        showFlash(`Invalid config JSON for ${getChannelLabel(channel)}: ${err.message}`, 'error');
        return;
      }
      const secretsText = secretsInput ? (secretsInput.value || '').trim() : '';
      let secrets;
      if (secretsText) {
        try {
          secrets = JSON.parse(secretsText);
        } catch (err) {
          showFlash(`Invalid secrets JSON for ${getChannelLabel(channel)}: ${err.message}`, 'error');
          return;
        }
      }
      let mailboxKeyForPayload;
      if (channel === 'email') {
        const selectedMailbox = channelSettingsState.selectedMailboxes.email || (mailboxSelector && mailboxSelector.value) || 'default';
        if (selectedMailbox === NEW_MAILBOX_OPTION) {
          const nextKey = mailboxKeyInput && mailboxKeyInput.value ? mailboxKeyInput.value.trim() : '';
          if (!nextKey) {
            showFlash('Enter a mailbox key before saving.', 'error');
            return;
          }
          if (!/^[a-z0-9_-]+$/i.test(nextKey)) {
            showFlash('Mailbox key must contain only letters, numbers, dashes, or underscores.', 'error');
            return;
          }
          mailboxKeyForPayload = nextKey;
        } else {
          mailboxKeyForPayload = selectedMailbox || 'default';
        }
      }

      const buttons = [];
      const saveBtn = channelConfigsContainer.querySelector(`button[data-config-save="${channel}"]`);
      const testBtn = channelConfigsContainer.querySelector(`button[data-config-validate="${channel}"]`);
      if (saveBtn) buttons.push(saveBtn);
      if (testBtn) buttons.push(testBtn);
      buttons.forEach((btn) => {
        btn.disabled = true;
        btn.dataset.originalText = btn.textContent;
        btn.textContent = validate ? 'Saving & Testing…' : 'Saving…';
      });
      configInput.disabled = true;
      if (secretsInput) secretsInput.disabled = true;
      if (mailboxKeyInput) mailboxKeyInput.disabled = true;
      if (mailboxSelector) mailboxSelector.disabled = true;
      const isPbx = channel === 'pbx';
      if (validate && isPbx) {
        setChannelStatus(channel, 'Testing PBX credentials…', 'info');
      }
      try {
        const payload = { config };
        if (secrets !== undefined) payload.secrets = secrets;
        if (validate) payload.validate = true;
        if (mailboxKeyForPayload && channel === 'email') {
          payload.mailbox_key = mailboxKeyForPayload;
        }
        const result = await adminAPI.fetch(API.adminChannelConfig(channel, mailboxKeyForPayload), {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        if (secretsInput) secretsInput.value = '';
        if (result && typeof result === 'object') {
          upsertChannelConfigState(result);
          if (channel === 'email') {
            channelSettingsState.selectedMailboxes.email = (result && result.mailbox_key) || 'default';
            renderChannelConfigs();
          } else {
            updateChannelConfigCard(result);
          }
        }
        showFlash(`${getChannelLabel(channel)} configuration saved${validate ? ' and test triggered' : ''}.`);
        if (isPbx) {
          const testedAtRaw = result && result.last_validated_at ? result.last_validated_at : new Date().toISOString();
          const testedAt = formatDateTimeFull(testedAtRaw);
          if (validate) {
            setChannelStatus(channel, `PBX token validated ${testedAt}`, 'success');
          } else {
            setChannelStatus(channel, `PBX configuration saved ${testedAt}`, 'info');
          }
        }
      } catch (err) {
        showFlash(err.message || 'Failed to save configuration', 'error');
        if (isPbx) {
          const detail = (err && (err.message || (err.body && err.body.detail))) || 'PBX test failed.';
          setChannelStatus(channel, detail, 'error');
        }
      } finally {
        configInput.disabled = false;
        if (secretsInput) secretsInput.disabled = false;
        if (mailboxKeyInput) mailboxKeyInput.disabled = false;
        if (mailboxSelector) mailboxSelector.disabled = false;
        buttons.forEach((btn) => {
          if (!btn) return;
          btn.disabled = false;
          btn.textContent = btn.dataset.originalText || (btn.dataset.configValidate ? 'Save & Test' : 'Save');
          delete btn.dataset.originalText;
        });
      }
    }

  export default {
    init: () => { resolveDom(); initChannelSettingsView(); },
    loadChannelConfigs,
    renderChannelConfigs,
    setChannelStatus,
    upsertChannelConfigState,
    getState: () => channelSettingsState,
    getStatusElements: () => channelStatusElements,
    getControlSync: () => channelControlSync,
  };
